import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import {
  AppContextType,
  ContactInfo,
  SkinScores,
  SkinScore,
  ViewName,
  SkinCondition,
  SnackbarProps,
  UserProfileWithDistributor,
  UserProfileDistributor,
  UserSession,
} from "../types";
import {
  TranslationData,
  ICountryData,
  IPrivacyPolicyDocument,
} from "../services/api";
import ApiService from "../services/api";
import fallbackTranslations from "../../public/locales/es.json";
import { cookieHelpers, FlowType, getCurrentFlow, getCookie, removeCookie, COOKIE_BASE_NAMES, getFlowCookieName } from "../utils/cookieUtils";
import { isRTL, getDirection, setDocumentDirection } from "../utils/rtlUtils";
import { VIEWS } from "../constants/views";

// Auth state reducer for atomic updates of token, userId, and role
interface AuthState {
  token: string | null;
  userId: string | null;
  role: 1 | 2 | null;
}

type AuthAction =
  | { type: 'SET_TOKEN'; payload: string | null }
  | { type: 'SET_USER_ID'; payload: string | null }
  | { type: 'SET_ROLE'; payload: 1 | 2 | null }
  | { type: 'SET_AUTH'; payload: AuthState }
  | { type: 'CLEAR_AUTH' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_USER_ID':
      return { ...state, userId: action.payload };
    case 'SET_ROLE':
      return { ...state, role: action.payload };
    case 'SET_AUTH':
      return action.payload;
    case 'CLEAR_AUTH':
      return { token: null, userId: null, role: null };
    default:
      return state;
  }
};

// Create context with default value
const AppContext = createContext<AppContextType | undefined>(undefined);

// Custom hook to use the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

// Provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // ============================================================================
  // UI STATE GROUP
  // ============================================================================
  // Manages application navigation, language, direction, and UI feedback
  
  const [currentView, setCurrentView] = useState<ViewName>(() => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/customer")
    ) {
      return VIEWS.HOME;
    }

    return VIEWS.DISTRIBUTOR_LOGIN;
  });

  // ============================================================================
  // SESSION STATE GROUP
  // ============================================================================
  // Manages user session tracking and session-related errors
  
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // ============================================================================
  // AUTH STATE GROUP
  // ============================================================================
  // Manages authentication: token, userId, role
  // Uses reducer for atomic updates and syncs with flow-specific cookies
  
  // Flow type state (determines which cookies to use: customer_* or distributor_*)
  const [flowType, setFlowType] = useState<FlowType>(getCurrentFlow());

  // Use reducer for auth state to ensure atomic updates
  const [authState, dispatchAuth] = useReducer(authReducer, {
    token: cookieHelpers.getToken(flowType),
    userId: cookieHelpers.getUserId(flowType),
    role: cookieHelpers.getRole(flowType),
  });

  // Distributor ID state (not persisted to cookies, only in memory)
  const [distributorId, setDistributorId] = useState<string | null>(null);
  // Hide distributor signup CTA on Contact after Try Now session fallback
  const [hideDistributorSignupCta, setHideDistributorSignupCta] = useState(false);

  // Use refs to track if we're currently syncing to prevent loops
  const isSyncingRef = useRef(false);

  // Language and direction state (part of UI state group)
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    return cookieHelpers.getLanguage(flowType);
  });

  // Direction state (RTL/LTR)
  const [direction, setDirection] = useState<'rtl' | 'ltr'>(() => {
    return getDirection(cookieHelpers.getLanguage(flowType));
  });

  // Computed RTL boolean
  const isRTLMode = isRTL(currentLanguage);

  // Helper functions to set token with flow-specific cookie sync
  const setTokenWithStorage = useCallback((newToken: string | null) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    
    dispatchAuth({ type: 'SET_TOKEN', payload: newToken });
    if (newToken) {
      cookieHelpers.setToken(newToken, flowType);
    } else {
      cookieHelpers.removeToken(flowType);
    }
    
    isSyncingRef.current = false;
  }, [flowType]);

  const setUserIdWithStorage = useCallback((newUserId: string | null) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    
    dispatchAuth({ type: 'SET_USER_ID', payload: newUserId });
    if (newUserId) {
      cookieHelpers.setUserId(newUserId, flowType);
    } else {
      cookieHelpers.removeUserId(flowType);
    }
    
    isSyncingRef.current = false;
  }, [flowType]);

  const setRoleWithStorage = useCallback((newRole: 1 | 2 | null) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    
    dispatchAuth({ type: 'SET_ROLE', payload: newRole });
    
    // Determine flow from role: 1 = distributor, 2 = customer
    const targetFlow: FlowType = newRole === 1 ? 'distributor' : 'customer';
    
    if (newRole) {
      cookieHelpers.setRole(newRole, targetFlow);
      setFlowType(targetFlow);
    } else {
      // Clear both flows when role is null
      cookieHelpers.removeRole('distributor');
      cookieHelpers.removeRole('customer');
    }
    
    isSyncingRef.current = false;
  }, []);

  // Helper function to set language with cookie sync and direction update
  const updateLanguage = useCallback((newLanguage: string) => {
    setCurrentLanguage(newLanguage);
    cookieHelpers.setLanguage(newLanguage, flowType);
    
    // Update direction based on new language
    const newDirection = getDirection(newLanguage);
    setDirection(newDirection);
    setDocumentDirection(newLanguage);
  }, [flowType]);

  // Sync state with cookies on mount (fixes stale state on page load)
  useEffect(() => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    
    const currentFlowType = getCurrentFlow();
    setFlowType(currentFlowType);
    
    const storedToken = cookieHelpers.getToken(currentFlowType);
    const storedUserId = cookieHelpers.getUserId(currentFlowType);
    const storedRole = cookieHelpers.getRole(currentFlowType);
    const storedLanguage = cookieHelpers.getLanguage(currentFlowType);
    
    const newAuthState: AuthState = {
      token: storedToken,
      userId: storedUserId,
      role: storedRole,
    };
    
    // Only update if different to avoid unnecessary re-renders
    if (
      storedToken !== authState.token ||
      storedUserId !== authState.userId ||
      newAuthState.role !== authState.role
    ) {
      dispatchAuth({ type: 'SET_AUTH', payload: newAuthState });
    }
    
    // Update language state if different
    if (storedLanguage !== currentLanguage) {
      setCurrentLanguage(storedLanguage);
    }

    // Set initial document direction
    const initialDirection = getDirection(storedLanguage);
    setDirection(initialDirection);
    setDocumentDirection(storedLanguage);
    
    // Clean up old non-flow-specific selectedLanguage cookie
    const oldLanguageCookie = getCookie(COOKIE_BASE_NAMES.SELECTED_LANGUAGE);
    if (oldLanguageCookie) {
      // If there's an old cookie and no flow-specific one, migrate it
      const flowSpecificCookie = getCookie(getFlowCookieName(COOKIE_BASE_NAMES.SELECTED_LANGUAGE, currentFlowType));
      if (!flowSpecificCookie) {
        cookieHelpers.setLanguage(oldLanguageCookie, currentFlowType);
      }
      // Remove the old non-flow-specific cookie
      removeCookie(COOKIE_BASE_NAMES.SELECTED_LANGUAGE);
    }
    
    isSyncingRef.current = false;
  }, []); // Run only on mount

  // Cross-tab logout synchronization using BroadcastChannel API
  useEffect(() => {
    const channelName = `herbalife_auth_${flowType}`;
    
    // Use BroadcastChannel for modern browsers
    let channel: BroadcastChannel | null = null;
    
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(channelName);
      
      channel.onmessage = (event) => {
        if (event.data.type === 'LOGOUT') {
          // Remove cookies for THIS flow in this tab
          cookieHelpers.removeToken(flowType);
          cookieHelpers.removeUserId(flowType);
          cookieHelpers.removeRole(flowType);
          
          // Clear auth state
          dispatchAuth({ type: 'CLEAR_AUTH' });
          setSessionId(null);
          
          // Redirect to appropriate login page
          if (flowType === 'distributor') {
            setCurrentView(VIEWS.DISTRIBUTOR_LOGIN);
          } else {
            setCurrentView(VIEWS.HOME);
          }
        } else if (event.data.type === 'LOGIN') {
          // Sync login across tabs
          const { token, userId, role } = event.data.payload;
          if (token && userId && role) {
            isSyncingRef.current = true;
            dispatchAuth({
              type: 'SET_AUTH',
              payload: { token, userId, role }
            });
            setTimeout(() => {
              isSyncingRef.current = false;
            }, 100);
          }
        }
      };
    }
    
    // Fallback: Use localStorage for older browsers
    const handleStorageChange = (e: StorageEvent) => {
      const logoutKey = `logout_${flowType}`;
      const loginKey = `login_${flowType}`;
      
      if (e.key === logoutKey && e.newValue) {
        // Remove cookies for THIS flow in this tab
        cookieHelpers.removeToken(flowType);
        cookieHelpers.removeUserId(flowType);
        cookieHelpers.removeRole(flowType);
        
        // Clear auth state
        dispatchAuth({ type: 'CLEAR_AUTH' });
        setSessionId(null);
        
        // Redirect to appropriate login page
        if (flowType === 'distributor') {
          setCurrentView(VIEWS.DISTRIBUTOR_LOGIN);
        } else {
          setCurrentView(VIEWS.HOME);
        }
        
        // Clean up the flag
        localStorage.removeItem(logoutKey);
      } else if (e.key === loginKey && e.newValue) {
        // Sync login across tabs
        try {
          const { token, userId, role } = JSON.parse(e.newValue);
          if (token && userId && role) {
            isSyncingRef.current = true;
            dispatchAuth({
              type: 'SET_AUTH',
              payload: { token, userId, role }
            });
            setTimeout(() => {
              isSyncingRef.current = false;
            }, 100);
          }
        } catch (error) {
          console.error('Error parsing login event:', error);
        }
        
        // Clean up the flag
        localStorage.removeItem(loginKey);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      if (channel) {
        channel.close();
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [flowType, setCurrentView, setSessionId]);

  // ============================================================================
  // CACHE STATE GROUP
  // ============================================================================
  // Manages cached data for user profiles, sessions, and analysis data
  // Includes loading states, errors, and last fetched timestamps
  
  // Consolidated user profiles cache (removed redundant userProfileDetails)
  const [userProfilesCache, setUserProfilesCache] = useState<{
    data: UserProfileWithDistributor[] | UserProfileDistributor[] | null;
    isLoading: boolean;
    error: string | null;
    lastFetched: Date | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
    lastFetched: null,
  });

  // Distributor sessions cache
  const [distributorSessionsCache, setDistributorSessionsCache] = useState<{
    data: UserSession[] | null;
    isLoading: boolean;
    error: string | null;
    lastFetched: Date | null;
    distributorId: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
    lastFetched: null,
    distributorId: null,
  });

  // Customer sessions cache (for distributor viewing customer listing)
  const [customerSessionsCache, setCustomerSessionsCache] = useState<{
    data: UserSession[] | null;
    isLoading: boolean;
    error: string | null;
    lastFetched: Date | null;
    distributorId: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
    lastFetched: null,
    distributorId: null,
  });

  const [analysisSessions, setAnalysisSessions] = useState<UserSession[] | null>(null);

  // ============================================================================
  // TRANSLATION STATE GROUP
  // ============================================================================
  // Manages translation data, privacy policy translations, and country data
  
  // Translation state
  const [translationData, setTranslationData] =
    useState<TranslationData | null>(null);

  // Privacy policy translation state
  const [privacyPolicyTranslationData, setPrivacyPolicyTranslationData] =
    useState<IPrivacyPolicyDocument | null>(null);

  // Country data state
  const [countryData, setCountryData] = useState<ICountryData[]>([]);

  // ============================================================================
  // APP-SPECIFIC STATE GROUP
  // ============================================================================
  // Manages application-specific state: user info, skin analysis, UI feedback
  
  // User information state
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: "",
    password: "",
  });

  // Skin analysis state
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [selfieImage, setSelfieImage] = useState<Blob | null>(null);
  const [skinScores, setSkinScores] = useState<SkinScores | null>(null);
  const [recommendations, setRecommendations] = useState<
    SkinCondition[] | null
  >(null);
  const [feedback, setFeedback] = useState<"GREEN" | "YELLOW" | "RED">("GREEN");
  
  // UI feedback state (part of UI state group but kept here for logical grouping)
  const [snackbar, setSnackbar] = useState<SnackbarProps>({
    open: false,
    message: "",
    severity: "success",
  });

  // Functions to update state
  const updateContactInfo = (info: Partial<ContactInfo>): void => {
    setContactInfo({ ...contactInfo, ...info });
  };

  // Session and table view state
  const [enableSessionToCreateNew, setEnableSessionToCreateNew] = useState<boolean>(false);
  const [distributorTableView, setDistributorTableView] = useState<'distributor' | 'customer'>('distributor');

  /** Pagination for DistributorUserDetails — keyed by distributorId so it persists across route changes */
  const [distributorUserDetailsPages, setDistributorUserDetailsPages] = useState<
    Record<string, { distributor: number; customer: number }>
  >({});

  const setDistributorUserDetailsPage = useCallback(
    (distId: string, view: 'distributor' | 'customer', page: number) => {
      setDistributorUserDetailsPages((prev) => {
        const existing = prev[distId] ?? { distributor: 1, customer: 1 };
        return {
          ...prev,
          [distId]: {
            ...existing,
            [view]: page,
          },
        };
      });
    },
    []
  );

  /**
   * Cached function to fetch user profiles (only calls API once)
   *
   * This function ensures that getUserProfiles API is only called once per session.
   * Subsequent calls will return cached data instead of making new API requests.
   *
   * Usage:
   * ```typescript
   * const { fetchUserProfiles, userProfilesCache } = useAppContext();
   *
   * // In your component
   * useEffect(() => {
   *   if (token && !userProfilesCache.data && !userProfilesCache.isLoading) {
   *     const api = new ApiService(null, token);
   *     fetchUserProfiles(api, role);
   *   }
   * }, [token, role, fetchUserProfiles, userProfilesCache.data, userProfilesCache.isLoading]);
   *
   * // Access cached data
   * const profiles = userProfilesCache.data;
   * const isLoading = userProfilesCache.isLoading;
   * const error = userProfilesCache.error;
   * ```
   *
   * @param apiService - The ApiService instance
   * @param role - User role (1: distributor, 2: customer)
   * @returns Promise with user profiles data
   */
  
  // Use ref to track ongoing fetch promise to prevent race conditions
  const fetchProfilesPromiseRef = useRef<Promise<UserProfileWithDistributor[] | UserProfileDistributor[]> | null>(null);
  
  const fetchUserProfiles = useCallback(
    async (
      apiService: ApiService,
      role?: 1 | 2
    ): Promise<UserProfileWithDistributor[] | UserProfileDistributor[]> => {
      // If already loading, return the existing promise instead of making duplicate requests
      if (fetchProfilesPromiseRef.current) {
        return fetchProfilesPromiseRef.current;
      }

      // Set loading state
      setUserProfilesCache((prev) => {
        // Check if already loading to prevent duplicate requests
        if (prev.isLoading) {
          return prev;
        }
        return {
          ...prev,
          isLoading: true,
          error: null,
        };
      });

      // Create the fetch promise and store it in ref
      fetchProfilesPromiseRef.current = (async () => {
        try {
          const response = await apiService.getUserProfiles(role);

          // Update cache with successful response
          setUserProfilesCache({
            data: response,
            isLoading: false,
            error: null,
            lastFetched: new Date(),
          });

          return response;
        } catch (error: any) {
          const errorMessage = error.message || "Failed to fetch user profiles";

          // Update cache with error
          setUserProfilesCache({
            data: null,
            isLoading: false,
            error: errorMessage,
            lastFetched: null,
          });

          throw error;
        } finally {
          // Clear the promise ref when done
          fetchProfilesPromiseRef.current = null;
        }
      })();

      return fetchProfilesPromiseRef.current;
    },
    [] // Empty dependency array - function doesn't depend on cache state
  );

  // Function to clear user profiles cache
  const clearUserProfilesCache = useCallback(() => {
    setUserProfilesCache({
      data: null,
      isLoading: false,
      error: null,
      lastFetched: null,
    });
  }, []);

  // Function to update distributor sessions cache
  const updateDistributorSessionsCache = useCallback(
    (
      data: UserSession[] | null,
      isLoading: boolean = false,
      error: string | null = null,
      distributorId: string | null = null
    ) => {
      setDistributorSessionsCache((prev) => ({
        ...prev,
        data,
        isLoading,
        error,
        lastFetched: data ? new Date() : prev.lastFetched,
        distributorId: distributorId || prev.distributorId,
      }));
    },
    []
  );

  // Function to clear distributor sessions cache
  const clearDistributorSessionsCache = useCallback(() => {
    setDistributorSessionsCache({
      data: null,
      isLoading: false,
      error: null,
      lastFetched: null,
      distributorId: null,
    });
  }, []);

  // Function to update customer sessions cache
  const updateCustomerSessionsCache = useCallback(
    (
      data: UserSession[] | null,
      isLoading: boolean = false,
      error: string | null = null,
      distributorId: string | null = null
    ) => {
      setCustomerSessionsCache((prev) => ({
        ...prev,
        data,
        isLoading,
        error,
        lastFetched: data ? new Date() : prev.lastFetched,
        distributorId: distributorId || prev.distributorId,
      }));
    },
    []
  );

  // Function to clear customer sessions cache
  const clearCustomerSessionsCache = useCallback(() => {
    setCustomerSessionsCache({
      data: null,
      isLoading: false,
      error: null,
      lastFetched: null,
      distributorId: null,
    });
  }, []);

  // Function to get all user sessions
  const getAllUserSessions = useCallback(
    async (apiService: ApiService, userId: string): Promise<UserSession[]> => {
      try {
        const response = await apiService.getAllUserSessions(userId);
        const session = response.data?.sessions;
        // API returns a single session, wrap it in an array for consistency
        return session ? [session] : [];
      } catch (error) {
        console.error("Error fetching user sessions:", error);
        return [];
      }
    },
    []
  );

  const updateRecommendations = (recs: SkinCondition[]): void => {
    setRecommendations(recs);
  };

  // Profile state (part of App-specific state group)
  const [profileExists, setProfileExists] = useState<boolean>(false);
  const [profileId, setProfileId] = useState<string>("");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  
  // Analysis output state (part of App-specific state group)
  const [outputScore, setOutputScore] = useState<SkinScore[] | null>(null);
  
  // Navigation state (part of UI state group)
  const [redirectAfterAuth, setRedirectAfterAuth] = useState<ViewName | null>(
    null
  );

  // Translation management functions
  const getTranslation = useCallback(
    (key: string, fallback?: string): string => {
      if (!translationData) {
        // Use eng.json as fallback when translationData is not available
        return (fallbackTranslations as Record<string, string>)[key] || fallback || key;
      }
      return translationData.translations[key] || fallback || key;
    },
    [translationData]
  );

  // Skin type translation function
  const getSkinTypeTranslation = useCallback(
    (skinType: string): string => {
      const skinTypeMap: { [key: string]: string } = {
        normal: "Txt176",
        dry: "Txt677",
        oily: "Txt678",
        combination: "Txt679",
        not_sure: "Txt680",
      };

      const translationKey = skinTypeMap[skinType?.toLowerCase()];
      return translationKey
        ? getTranslation(translationKey)
        : skinType || "N/A";
    },
    [getTranslation]
  );

  // Gender translation function
  const getGenderTranslation = useCallback(
    (gender: string): string => {
      const genderMap: { [key: string]: string } = {
        male: "Txt683",
        female: "Txt684",
        other: "Txt685",
        not_sure: "Txt686",
      };

      const translationKey = genderMap[gender?.toLowerCase()];
      return translationKey
        ? getTranslation(translationKey)
        : formatLabel(gender);
    },
    [getTranslation]
  );

  // Format label utility function
  const formatLabel = useCallback((value?: string): string => {
    return (value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  // Privacy policy translation function
  const getPrivacyPolicyTranslation = useCallback(
    (
      section:
        | "distributor_privacy_policy"
        | "disclaimer"
        | "data_privacy_terms"
        | "orbo_privacy_policy",
      type: "text" | "html" = "text"
    ): string => {
      if (
        !privacyPolicyTranslationData ||
        !privacyPolicyTranslationData.privacy_policy
      ) {
        return "";
      }

      const sectionData = privacyPolicyTranslationData.privacy_policy[section];
      if (!sectionData) {
        return "";
      }

      return type === "html"
        ? sectionData.content_html
        : sectionData.content_text;
    },
    [privacyPolicyTranslationData]
  );

  // Function to fetch privacy policy translations
  const fetchPrivacyPolicyTranslations = useCallback(
    async (apiService: ApiService, language: string = "es") => {
      try {
        const response = await apiService.getPrivacyPolicyTranslations(
          language
        );
        if (response.data) {
          setPrivacyPolicyTranslationData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch privacy policy translations:", error);
      }
    },
    []
  );

  // Centralized function to load translations
  // Called when user selects/changes language - stores data in context for reuse
  const loadTranslations = useCallback(
    async (language?: string): Promise<void> => {
      const requestedLanguage = language || cookieHelpers.getLanguage(flowType);

      // If no language is provided and no saved language exists, return early
      // This allows IP detection to set the language first
      if (!requestedLanguage) {
        return;
      }
      
      // Check if translations already exist for the requested language
      // If yes, skip API call and use cached data
      if (translationData && translationData.language === requestedLanguage) {
        return;
      }

      try {
        const apiService = new ApiService();
        const translationsResponse = await apiService.getTranslations(requestedLanguage);
        
        if (translationsResponse.data) {
          // Store translation data in context - will be reused by getTranslation
          setTranslationData(translationsResponse.data);
        } else {
          // Fallback if API doesn't return data
          setTranslationData({
            translations: fallbackTranslations,
            language: "es",
            count: Object.keys(fallbackTranslations).length
          });
        }
      } catch (error) {
        console.error('[AppContext] Error loading translations:', error);
        setTranslationData({
          translations: fallbackTranslations,
          language: "es",
          count: Object.keys(fallbackTranslations).length
        });
      }
    },
    [translationData, flowType, setTranslationData]
  );

  // Broadcast logout to other tabs of the SAME flow only
  const broadcastLogout = useCallback(() => {
    const channelName = `herbalife_auth_${flowType}`;
    
    // Use BroadcastChannel if available
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(channelName);
      channel.postMessage({ type: 'LOGOUT' });
      channel.close();
    }
    
    // Fallback: Use localStorage event for older browsers
    const logoutKey = `logout_${flowType}`;
    localStorage.setItem(logoutKey, Date.now().toString());
    setTimeout(() => {
      localStorage.removeItem(logoutKey);
    }, 100);
  }, [flowType]);

  // Broadcast login to other tabs
  const broadcastLogin = useCallback((token: string, userId: string, role: 1 | 2) => {
    const channelName = `herbalife_auth_${flowType}`;
    
    // Use BroadcastChannel if available
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(channelName);
      channel.postMessage({ 
        type: 'LOGIN',
        payload: { token, userId, role }
      });
      channel.close();
    }
    
    // Fallback: Use localStorage event for older browsers
    const loginKey = `login_${flowType}`;
    localStorage.setItem(loginKey, JSON.stringify({ token, userId, role }));
    setTimeout(() => {
      localStorage.removeItem(loginKey);
    }, 1000);
  }, [flowType]);

  // Clear method to reset all state values
  const clearAppState = (): void => {
    // Broadcast logout to other tabs of the SAME flow only
    broadcastLogout();
    
    const _role = authState.role;
    if (authState.role === 1) {
      setDistributorId(null);
    }
    setHideDistributorSignupCta(false);

    const pathname = window.location.pathname;
    setCurrentView(
      pathname.includes("customer") ? VIEWS.HOME : VIEWS.DISTRIBUTOR_LOGIN
    );

    setSessionId(null);
    setSessionError(null);
    
    // Clear auth state for CURRENT flow only (not both flows)
    // This keeps the other flow's session active
    cookieHelpers.removeToken(flowType);
    cookieHelpers.removeUserId(flowType);
    cookieHelpers.removeRole(flowType);
    
    // Clear local state
    dispatchAuth({ type: 'CLEAR_AUTH' });
    
    setContactInfo({
      email: "",
      password: "",
    });

    // Clear user profiles cache
    clearUserProfilesCache();

    setOtpVerified(false);
    setSelfieImage(null);
    setSkinScores(null);
    setRecommendations(null);
    setFeedback("GREEN");
    setSnackbar({
      open: false,
      message: "",
      severity: "success",
    });
    setProfileExists(false);
    setProfileId("");
    setOutputScore(null);
    setAnalysisSessions(null);
    setRedirectAfterAuth(null);
    setTranslationData(null);
    setDistributorTableView('distributor');
    setDistributorUserDetailsPages({});
    if (_role === 1 && !pathname.includes('/distributor/successful')) {
      window.location.href = import.meta.env.VITE_LOGOUT_REDIRECT;
    }
  };

  // Value to be provided to consuming components
  const contextValue: AppContextType = {
    currentView,
    setCurrentView, //: memoizedSetCurrentView,
    contactInfo,
    updateContactInfo,
    otpVerified,
    setOtpVerified,
    selfieImage,
    setSelfieImage,
    skinScores,
    setSkinScores,
    recommendations,
    updateRecommendations,
    feedback,
    setFeedback,
    sessionId,
    setSessionId,
    sessionError,
    setSessionError,
    token: authState.token,
    setToken: setTokenWithStorage,
    userId: authState.userId,
    setUserId: setUserIdWithStorage,
    snackbar,
    setSnackbar,
    profileExists,
    setProfileExists,
    profileId,
    setProfileId,
    profileImageUrl,
    setProfileImageUrl,
    outputScore,
    setOutputScore,
    distributorId,
    setDistributorId,
    hideDistributorSignupCta,
    setHideDistributorSignupCta,
    role: authState.role,
    setRole: setRoleWithStorage,
    clearAppState,
    redirectAfterAuth,
    setRedirectAfterAuth,
    // Flow and language management
    flowType,
    currentLanguage,
    updateLanguage,
    // RTL/LTR direction management
    direction,
    isRTL: isRTLMode,
    // User profiles cache management
    fetchUserProfiles,
    clearUserProfilesCache,
    userProfilesCache,
    // Distributor sessions cache management
    distributorSessionsCache,
    updateDistributorSessionsCache,
    clearDistributorSessionsCache,
    // Customer sessions cache management
    customerSessionsCache,
    updateCustomerSessionsCache,
    clearCustomerSessionsCache,
    analysisSessions,
    setAnalysisSessions,
    getAllUserSessions,
    // Translation management
    translationData,
    setTranslationData,
    getTranslation,
    loadTranslations,
    getSkinTypeTranslation,
    getGenderTranslation,
    formatLabel,
    // Privacy policy translation management
    privacyPolicyTranslationData,
    setPrivacyPolicyTranslationData,
    getPrivacyPolicyTranslation,
    fetchPrivacyPolicyTranslations,
    // Country data management
    countryData,
    setCountryData,
    // Cross-tab sync
    broadcastLogout,
    broadcastLogin,
    // Session creation control
    enableSessionToCreateNew,
    setEnableSessionToCreateNew,
    distributorTableView,
    setDistributorTableView,
    distributorUserDetailsPages,
    setDistributorUserDetailsPage,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
